import txfLoadingLightUrl from '@/assets/misc/txf_loading_light.gif'
import txfLoadingDarkUrl from '@/assets/misc/txf_loading_dark.gif'
import { observer } from 'mobx-react-lite'
import { useLoaderData } from 'react-router'
import { useActivation } from '@/hooks/view-model-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import {
  useHideGlobalNavbar,
  useProvideViewportBackground,
} from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { links } from '@/utils/link'
import {
  type TaxPrepOrganizerViewModel,
  OrganizerStep,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerViewModel'
import type { TaxPrepOrganizerWrapperViewModel } from '@/features/jobs/details/tax-type/tax-prep-organizer/TaxPrepOrganizerWrapperViewModel'
import {
  InfoPanel,
  InfoPanelContextProvider,
  useInfoPanelContext,
} from '@/features/jobs/details/tax-type/tax-prep-organizer/components/InfoPanel'
import { StepNavigation } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/StepNavigation'
import { YearTabs } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/YearTabs'
import { PersonalStepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/PersonalStepCardListContainer'
import { BusinessStepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/BusinessStepCardListContainer'
import { RentalStepCardListContainer } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/RentalStepCardListContainer'
import { AddDependentDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/personal/components/AddDependentDialog'
import { AddBusinessDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/business/components/AddBusinessDialog'
import { AddRentalDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/rentals/components/AddRentalDialog'
import { SaveSectionDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/SaveSectionDialog'
import { SwitchYearDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/SwitchYearDialog'
import { ConnectWithProDialog } from '@/features/jobs/details/tax-type/tax-prep-organizer/components/ConnectWithProDialog'
import styles from './TaxPrepOrganizerView.module.css'
import {
  ScreenHeaderBorder,
  ScreenHeaderContent,
  ScreenHeaderMain,
  ScreenHeaderPersistentTopBar,
  ScreenHeaderPersistentTopBarLayout,
  ScreenHeaderPersistentTopBarTitle,
  ScreenHeaderProvider,
  useScreenHeaderTotalOffset,
} from '@/components/ScreenHeader'
import type { CSSProperties, ReactNode } from 'react'
import { usePageTopOffset } from '@/contexts/page-top-offset-context'
import cx from '@/utils/classnames'
import { SectionHeadline } from '@/components/SectionHeadline'
import { countdownFormatters } from '@/utils/date-util'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'

interface LoaderData {
  vm: TaxPrepOrganizerWrapperViewModel
}

interface StepCardProps {
  vm: TaxPrepOrganizerViewModel
  testId?: string
}

const STEP_CARDS: Record<OrganizerStep, React.ComponentType<StepCardProps>> = {
  [OrganizerStep.Personal]: PersonalStepCardListContainer,
  [OrganizerStep.Business]: BusinessStepCardListContainer,
  [OrganizerStep.Rental]: RentalStepCardListContainer,
}

const testId = 'tax-prep-organizer-view'

export default observer(function TaxPrepOrganizerView() {
  useHideGlobalNavbar(true)
  useProvideViewportBackground('secondary')
  useDocumentTitle('Tax Prep Organizer')

  const { vm: wrapperVm } = useLoaderData<LoaderData>()
  useActivation(wrapperVm)

  return (
    <InfoPanelContextProvider>
      <ScreenHeaderProvider applyDocumentScrollPadding>
        <Page testId={testId}>
          {!wrapperVm.showLoading ? (
            <>
              <TaxPrepOrganizerScreenHeader
                wrapperVm={wrapperVm}
                testId={testId}
              />
              <TaxPrepOrganizerLayoutContainer wrapperVm={wrapperVm} />
            </>
          ) : (
            <LoadingLayoutContainer vm={wrapperVm} />
          )}
        </Page>
      </ScreenHeaderProvider>
    </InfoPanelContextProvider>
  )
})

interface PageProps {
  children?: ReactNode
  testId?: string
}

export function Page({ children, testId }: PageProps) {
  const pageTopOffset = usePageTopOffset()
  const totalOffset = useScreenHeaderTotalOffset()
  return (
    <div
      className={styles.page}
      style={
        {
          '--page-top-offset': `${pageTopOffset}px`,
          '--top-bar-total-offset': `${totalOffset}px`,
        } as CSSProperties
      }
      data-test={testId}
    >
      {children}
    </div>
  )
}

const LoadingLayoutContainer = observer(function LoadingLayoutContainer({
  vm,
}: {
  vm: TaxPrepOrganizerWrapperViewModel
}) {
  return (
    <LoadingLayout>
      <LoadingImage />
      <SectionHeadline>We are loading your information</SectionHeadline>
      <LoadingCountdown timeInSeconds={vm.timer} />
    </LoadingLayout>
  )
})

function LoadingLayout({ children }: { children?: ReactNode }) {
  return <div className={styles.loadingLayout}>{children}</div>
}

function LoadingImage() {
  const lightDark = useLightDarkFn()
  const loadingGifUrl = lightDark(txfLoadingLightUrl, txfLoadingDarkUrl)

  return <img src={loadingGifUrl} alt="Loading" width={294} height={160} />
}

function LoadingCountdown({ timeInSeconds }: { timeInSeconds: number }) {
  if (timeInSeconds < 0) {
    return null
  }

  return (
    <p className={styles.loadingCountdown}>
      Please stay on this page and allow up to{' '}
      <span className={styles.loadingCountdownValue}>
        {countdownFormatters.minuteSecond(timeInSeconds)} minutes
      </span>{' '}
      for data to process.
    </p>
  )
}

interface TaxPrepOrganizerLayoutContainerProps {
  wrapperVm: TaxPrepOrganizerWrapperViewModel
}

const TaxPrepOrganizerLayoutContainer = observer(
  function TaxPrepOrganizerLayoutContainer({
    wrapperVm,
  }: TaxPrepOrganizerLayoutContainerProps) {
    const isSpacious = useIsSpacious()

    return (
      <TaxPrepOrganizerLayout>
        <TaxPrepOrganizerMainArea>
          {wrapperVm.innerVm && (
            <MainAreaContent vm={wrapperVm.innerVm} testId={testId} />
          )}
        </TaxPrepOrganizerMainArea>

        <TaxPrepOrganizerPanelArea>
          <InfoPanel
            variant={isSpacious ? 'inline' : 'modal'}
            testId={`${testId}-info-panel`}
          />
        </TaxPrepOrganizerPanelArea>

        <SwitchYearDialog
          vm={wrapperVm.switchYearDialogVm}
          testId={`${testId}-switch-year-dialog`}
        />
        <ConnectWithProDialog
          vm={wrapperVm.connectWithProDialogVm}
          testId={`${testId}-connect-with-pro-dialog`}
        />
      </TaxPrepOrganizerLayout>
    )
  },
)

export function TaxPrepOrganizerLayout({ children }: { children?: ReactNode }) {
  const { isOpen } = useInfoPanelContext()
  return (
    <div className={cx(styles.layout, isOpen && styles.open)}>{children}</div>
  )
}

export function TaxPrepOrganizerMainArea({
  children,
}: {
  children?: ReactNode
}) {
  return <main className={styles.mainArea}>{children}</main>
}

export function TaxPrepOrganizerPanelArea({
  children,
}: {
  children?: ReactNode
}) {
  return <div className={styles.panelArea}>{children}</div>
}

interface MainAreaContentProps {
  vm: TaxPrepOrganizerViewModel
  testId: string
}

const MainAreaContent = observer(function MainAreaContent({
  vm,
  testId,
}: MainAreaContentProps) {
  const StepCard = STEP_CARDS[vm.selectedStep]

  return (
    <>
      <StepNavigation
        activeStep={vm.selectedStep}
        onStepChange={vm.selectStep}
        testId={`${testId}-step-navigation`}
      />

      <StepCard vm={vm} testId={testId} />

      <AddDependentDialog
        vm={vm.addDependentDialogVm}
        testId={`${testId}-add-dependent-dialog`}
      />
      <AddBusinessDialog
        vm={vm.addBusinessDialogVm}
        testId={`${testId}-add-business-dialog`}
      />
      <AddRentalDialog
        vm={vm.addRentalDialogVm}
        testId={`${testId}-add-rental-dialog`}
      />
      <SaveSectionDialog
        vm={vm.saveSectionDialogVm}
        testId={`${testId}-save-section-dialog`}
      />
    </>
  )
})

interface TaxPrepOrganizerScreenHeaderProps {
  wrapperVm: TaxPrepOrganizerWrapperViewModel
  testId: string
}

const TaxPrepOrganizerScreenHeader = observer(
  function TaxPrepOrganizerScreenHeader({
    wrapperVm,
    testId,
  }: TaxPrepOrganizerScreenHeaderProps) {
    const isSpacious = useIsSpacious()

    return (
      <>
        <ScreenHeaderPersistentTopBar>
          <ScreenHeaderPersistentTopBarLayout>
            <ScreenHeaderPersistentTopBarTitle visibility="always">
              <ScreenHeaderTopBarWrapper>
                <ScreenHeaderTopBarLeft>
                  <YearTabs
                    years={wrapperVm.activeFilingYears}
                    selectedYear={wrapperVm.selectedYear}
                    onYearChange={wrapperVm.selectYear}
                    compact={
                      !isSpacious || wrapperVm.activeFilingYears.length > 3
                    }
                    testId={`${testId}-year-tabs`}
                  />
                </ScreenHeaderTopBarLeft>
                <ScreenHeaderTopBarRight>
                  <ButtonGroup>
                    {wrapperVm.showConnectWithAPro && (
                      <Button
                        variant="text"
                        label="Connect with a Pro"
                        onPress={wrapperVm.showConnectWithProDialog}
                        isPending={
                          wrapperVm.connectWithProDialogVm.confirm.pending
                        }
                        testId={`${testId}-connect-with-pro-button`}
                      />
                    )}
                    <Button
                      variant="outline"
                      label="Back"
                      iconLeft="back"
                      to={links.jobDetails(wrapperVm.jobId)}
                      testId={`${testId}-exit-button`}
                    />
                  </ButtonGroup>
                </ScreenHeaderTopBarRight>
              </ScreenHeaderTopBarWrapper>
            </ScreenHeaderPersistentTopBarTitle>
          </ScreenHeaderPersistentTopBarLayout>
        </ScreenHeaderPersistentTopBar>
        <ScreenHeaderMain>
          <ScreenHeaderContent></ScreenHeaderContent>
          <ScreenHeaderBorder visibility="always" />
        </ScreenHeaderMain>
      </>
    )
  },
)

export function ScreenHeaderTopBarWrapper({
  children,
}: {
  children?: ReactNode
}) {
  return <div className={styles.topBarWrapper}>{children}</div>
}

export function ScreenHeaderTopBarLeft({ children }: { children?: ReactNode }) {
  return <div className={styles.topBarLeft}>{children}</div>
}

export function ScreenHeaderTopBarRight({
  children,
}: {
  children?: ReactNode
}) {
  return <div className={styles.topBarRight}>{children}</div>
}
